import { useMemo } from "react";
import { getSalePaymentBalances } from "../../../utils/saleBalances";

// ---------- Types ----------
interface PaymentTransaction {
    id: number;
    saleId: number;
    paymentLinkId: number | null;
    referenceNumber: string;
    paymentMethod: "CASH" | "CARD" | "ONLINE" | string;
    paidOnline: boolean;
    subtotalCents: number;
    taxCents: number;
    tipsCents: number;
    totalCents: number;
    createdAt: string;
    updatedAt: string;
    last4: string | null;
    cardType: string | null;
    cardRef: string | null;
    preAuth?: boolean;
    pre_auth?: boolean;
    isDeposit?: boolean;
    is_deposit?: boolean;
    total_cents?: number;
    status: "CHARGED" | "PENDING" | "FAILED" | string;
}

interface Sale {
    id: number;
    accountId: number;
    status: "OPEN" | "CLOSED" | "CANCELLED" | string;
    clientId: number;
    createdById: number;
    subtotalCents: number;
    subtotal_cents?: number;
    totalCents?: number;
    total_cents?: number;
    createdAt: string;
    updatedAt: string;
    archivedAt: string | null;
    presaleTipsCents: number;
    dualPricingMethod: "NONE" | string;
    paymentTransactions?: PaymentTransaction[];
    payment_transactions?: PaymentTransaction[];
}

interface Appointment {
    id: number;
    sale?: Sale | null;
    appointment?: { sale?: Sale | null };
}

const centsToDollars = (cents: number) => (cents || 0) / 100;

// ---------- Hook ----------
export const useAppointmentSaleDetails = (selectedAppointment: Appointment | null) => {
    return useMemo(() => {
        const sale = selectedAppointment?.sale || selectedAppointment?.appointment?.sale;
        if (!sale) return null;

        const txs = sale.paymentTransactions || sale.payment_transactions || [];
        const payment = txs[0];

        // Same balance math as the payment panel: card fees on earlier
        // payments are netted out, so Pending matches what checkout charges.
        const {
            paidCents,
            remainingCents: pendingCents,
            fullTotalCents,
        } = getSalePaymentBalances(sale);
        const saleSubtotalCents = sale.subtotalCents ?? sale.subtotal_cents ?? 0;

        const subtotal = centsToDollars(saleSubtotalCents);
        const total = centsToDollars(fullTotalCents);
        const paid = centsToDollars(paidCents);
        const pending = centsToDollars(pendingCents);
        const paymentStatus = payment?.status || "UNKNOWN";
        const paymentMethod = payment?.paymentMethod || "UNKNOWN";
        const isPaid = pendingCents <= 0 && paidCents > 0;
        const isOpen = sale.status === "OPEN";
        const isClosed = sale.status === "CLOSED";
        const hasDeposit = txs.some((t) => t.isDeposit || t.is_deposit);
        const hasBalanceDue = pendingCents > 0 && (hasDeposit || paidCents > 0);

        return {
            saleId: sale.id,
            saleStatus: sale.status,
            subtotal,
            total,
            paid,
            pending,
            hasDeposit,
            hasBalanceDue,
            paymentStatus,
            paymentMethod,
            isPaid,
            isClosed,
            isOpen,
            referenceNumber: payment?.referenceNumber,
            clientId: sale.clientId,
            accountId: sale.accountId,
        };
    }, [selectedAppointment]);
};
